// Vendor Imports
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import range from 'lodash/range';
import uniqueId from 'lodash/uniqueId';
import moment from 'moment';
import type React from 'react';
import { useState } from 'react';
import {
  ForgeDatePicker,
  ForgeDateRangePicker,
  ForgeRadio,
  ForgeTextField,
  ForgeSelect,
} from '@tylertech/forge-react';

// Project Imports
import Picklist, { type PicklistOption, PicklistSizes } from 'common/components/Picklist';
import { FilterFooter } from '../FilterFooter';
import * as CalendarDateFilter from '../lib/Filters/CalendarDateFilter';
import { useFocusFirstActionableElement } from './InputFocus';
import { I18n } from 'common/i18n';

// Constants
import {
  DATE_FORMAT,
  FILTER_TYPES,
  RELATIVE_FILTERS,
  RELATIVE_FILTER_VALUES,
  SINGLE_SELECT_BY,
  getFiscalYearStartMoment,
  getFiscalEndYear,
  getSelectByYearStartAndEndDates,
  getSelectByFiscalYearStartAndEndDates,
} from 'common/dates';
import { type FilterColumnsMap, type FilterEditorProps } from '../types';
import * as BaseFilter from '../lib/Filters/BaseFilter';

const CUSTOM_PERIOD_INPUT_FIELD = { MIN: 1, STEP: 1 };
const scope = 'shared.components.filter_bar.calendar_date_filter';

export interface CalendarDateFilterEditorProps extends FilterEditorProps {
  appliedFilter: CalendarDateFilter.CalendarDateFilterConfig;
}

interface DateOption {
  title: string;
  value: number;
}

/** The subset of filter arguments that `getCalendarDateRangeFilter` accepts as an override. */
type FilterArgumentOverrides = Parameters<typeof CalendarDateFilter.getCalendarDateRangeFilter>[1];

function getMoment(value?: string) {
  if (value === RELATIVE_FILTERS.TODAY) {
    return moment().startOf('day');
  } else if (value === RELATIVE_FILTERS.YESTERDAY) {
    return moment().subtract(1, 'days').startOf('day');
  } else {
    return moment(value);
  }
}

function getSingleSelectBy(filter: CalendarDateFilter.CalendarDateFilterConfig) {
  return typeof filter.singleSelect === 'object' ? filter.singleSelect.by : null;
}

function getMonthOptions(): DateOption[] {
  return range(0, 12).map((month) => ({
    title: I18n.t(`month_${month}`, { scope }),
    value: month,
  }));
}

function getYearOptions(
  columns: FilterColumnsMap,
  dirtyFilter: CalendarDateFilter.CalendarDateFilterConfig,
): DateOption[] {
  const rangeMinMoment = moment(CalendarDateFilter.getMinValue(columns), DATE_FORMAT);
  const rangeMaxMoment = moment(CalendarDateFilter.getMaxValue(columns), DATE_FORMAT);
  let rangeMinYear = rangeMinMoment.year();
  let rangeMaxYear = rangeMaxMoment.year();

  if (getSingleSelectBy(dirtyFilter) === SINGLE_SELECT_BY.FISCAL_YEAR) {
    const fiscalYearMoment = getFiscalYearStartMoment();
    // If a data point occurs after the FY month, its actually in the next fiscal year, so add 1.
    // eg. Government fiscal year runs October to September, so 2018-11-15 is in govt FY 2019.
    rangeMinYear += rangeMinMoment.dayOfYear() >= fiscalYearMoment.dayOfYear() ? 1 : 0;
    rangeMaxYear += rangeMaxMoment.dayOfYear() >= fiscalYearMoment.dayOfYear() ? 1 : 0;
  }

  return range(rangeMinYear, rangeMaxYear + 1).map((year) => ({
    title: `${year}`,
    value: year,
  }));
}

/**
 * Converts the `{ title, value }` options from {@link getMonthOptions} and {@link getYearOptions}
 * into the `{ label, value }` shape that ForgeSelect expects.
 */
function toSelectOptions(options: DateOption[], dataTestIdPrefix: string) {
  return options.map((option, index) => ({
    label: option.title,
    value: option.value.toString(),
    elementAttributes: new Map([['data-testId', `${dataTestIdPrefix}-${index}`]]),
  }));
}

function onKeyDownInputBox(event: React.KeyboardEvent) {
  if (event.key === '.') {
    event.preventDefault();
    event.stopPropagation();
  }
}

function getInitialDirtyFilter({
  appliedFilter,
  columns,
  isReadOnly,
  relativeDateOptions,
}: Pick<CalendarDateFilterEditorProps, 'appliedFilter' | 'columns' | 'isReadOnly' | 'relativeDateOptions'>) {
  const filterArgs = appliedFilter.arguments;
  let calendarDateFilterType = filterArgs?.calendarDateFilterType ?? FILTER_TYPES.RANGE;
  const rangeMinStartDay = CalendarDateFilter.getRangeMinStartDay(columns);
  const rangeMaxEndDay = CalendarDateFilter.getRangeMaxEndDay(columns);

  const firstRelativeDateOption = relativeDateOptions?.[0];
  let start = CalendarDateFilter.getDate(filterArgs?.start ?? rangeMinStartDay);
  let end = CalendarDateFilter.getDate(filterArgs?.end ?? rangeMaxEndDay);
  const firstRelativeDateOptionValue = firstRelativeDateOption?.value ?? RELATIVE_FILTERS.TODAY;
  const defaultFilterOption = RELATIVE_FILTER_VALUES[firstRelativeDateOptionValue];

  const relativeDatePeriod = {
    period: filterArgs?.period ?? defaultFilterOption.period,
    value: filterArgs?.value ?? defaultFilterOption.value,
    type: filterArgs?.type ?? defaultFilterOption.type,
  };

  const singleSelectBy = getSingleSelectBy(appliedFilter);

  // If we are in single select mode, modify the state to ensure
  // the end dates are set properly. When date filters are converted to queries,
  // a day is added to the end date, and the query is of the form ">= START and < END",
  // effectively making the query end-date-inclusive. For that reason, END should be
  // set to the last date we actually want included in the filter (which means subtracting
  // one day from the SINGLE_SELECT options).
  if (singleSelectBy === SINGLE_SELECT_BY.YEAR) {
    [start, end] = getSelectByYearStartAndEndDates(getMoment(start));
  } else if (singleSelectBy === SINGLE_SELECT_BY.FISCAL_YEAR) {
    [start, end] = getSelectByFiscalYearStartAndEndDates(getMoment(end));
  } else if (singleSelectBy === SINGLE_SELECT_BY.MONTH) {
    const startMoment = getMoment(start).date(1);
    start = startMoment.format(DATE_FORMAT);
    end = startMoment.add(1, 'month').subtract(1, 'day').format(DATE_FORMAT);
  } else if (singleSelectBy === SINGLE_SELECT_BY.DAY) {
    end = CalendarDateFilter.getDate(start);

    // Set calendarDateFilterType by prop since this handle both
    // a single select day and relative "Today" filter
    const existingCalendarDateFilterType = filterArgs?.calendarDateFilterType ?? FILTER_TYPES.RANGE;

    // Combination of SINGLE_SELECT_BY.DAY and FILTER_TYPES.RELATIVE
    // means this is a relative "Today" filter.
    // In View mode only, treat it as if this is a normal SINGLE_SELECT_BY.DAY
    // filter but with current date selected.
    if (isReadOnly && existingCalendarDateFilterType === FILTER_TYPES.RELATIVE) {
      start = getMoment(RELATIVE_FILTERS.TODAY).format(DATE_FORMAT);
      end = start;
      calendarDateFilterType = FILTER_TYPES.RANGE;
    }
  }

  return CalendarDateFilter.getCalendarDateRangeFilter(appliedFilter, {
    ...relativeDatePeriod,
    start,
    end,
    calendarDateFilterType,
  });
}

export function CalendarDateFilterEditor(props: CalendarDateFilterEditorProps) {
  const {
    appliedFilter,
    columns,
    dataProvider,
    isReadOnly,
    onRemove,
    onUpdate,
    popupRef,
    relativeDateOptions,
    showRemoveButtonInFooter = false,
  } = props;

  const [dirtyFilter, setDirtyFilter] = useState(() => getInitialDirtyFilter(props));
  const [prevAppliedFilter, setPrevAppliedFilter] = useState(appliedFilter);
  const dateFilterRef = useFocusFirstActionableElement<HTMLDivElement>();

  if (!isEqual(appliedFilter, prevAppliedFilter)) {
    setPrevAppliedFilter(appliedFilter);
    setDirtyFilter(getInitialDirtyFilter(props));
  }

  const updateDirtyFilter = (argumentOverrides: FilterArgumentOverrides) => {
    setDirtyFilter(CalendarDateFilter.getCalendarDateRangeFilter(dirtyFilter, argumentOverrides));
  };

  const isDirty = () => !isEqual(dirtyFilter, appliedFilter);

  const applyFilter = () => {
    onUpdate(dirtyFilter);
  };

  const resetFilter = () => {
    onUpdate(BaseFilter.reset(dirtyFilter, columns));
  };

  const onChangeSingleSelectDay = (date?: string) => {
    if (!date) {
      return;
    }

    updateDirtyFilter({
      start: CalendarDateFilter.getDate(date),
      end: CalendarDateFilter.getDate(date),
    });
  };

  const onChangeSingleSelectMonthMonth = (option: PicklistOption) => {
    const startMoment = moment(dirtyFilter.arguments?.start).month(option.value);
    const start = startMoment.format(DATE_FORMAT);
    const end = startMoment.add(1, 'month').subtract(1, 'day').format(DATE_FORMAT);

    updateDirtyFilter({ start, end });
  };

  const onChangeSingleSelectMonthYear = (option: PicklistOption) => {
    const startMoment = moment(dirtyFilter.arguments?.start).year(option.value);
    const start = startMoment.format(DATE_FORMAT);
    const end = startMoment.add(1, 'month').subtract(1, 'day').format(DATE_FORMAT);

    updateDirtyFilter({ start, end });
  };

  const onChangeSingleSelectYear = (option?: PicklistOption | null) => {
    if (!option) {
      return;
    }

    let start;
    if (getSingleSelectBy(dirtyFilter) === SINGLE_SELECT_BY.FISCAL_YEAR) {
      // The start of a fiscal year is actually the previous calendar year
      // e.g. 2018-09-01 is the start of the 2019 govt fiscal year
      start = getFiscalYearStartMoment()
        .year(option.value - 1)
        .format(DATE_FORMAT);
    } else {
      start = CalendarDateFilter.getDate(`${option.value}-01-01`);
    }
    const end = moment(start, DATE_FORMAT).add(1, 'year').subtract(1, 'day').format(DATE_FORMAT);

    const updatedFilter = CalendarDateFilter.getCalendarDateRangeFilter(dirtyFilter, { start, end });

    setDirtyFilter(updatedFilter);
    onUpdate(updatedFilter);
  };

  const onChangeDateRangeToToday = (detail: string) => {
    const startMoment = getMoment(detail);

    if (!startMoment.isBefore(moment())) {
      return;
    }

    updateDirtyFilter({ start: CalendarDateFilter.getDate(detail), end: 'today' });
  };

  const onChangeDateRange = (detail: { from: string; to: string }) => {
    const startMoment = getMoment(detail.from);
    const endMoment = getMoment(detail.to);

    if (!startMoment.isSameOrBefore(endMoment)) {
      return;
    }

    updateDirtyFilter({
      start: CalendarDateFilter.getDate(detail.from),
      end: CalendarDateFilter.getDate(detail.to),
    });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDirtyFilter({ calendarDateFilterType: event.target.value as FILTER_TYPES });
  };

  const renderSingleSelectDayPicker = () => {
    const calendarDateFilterType = dirtyFilter.arguments?.calendarDateFilterType ?? FILTER_TYPES.RANGE;

    const startMoment = getMoment(dirtyFilter.arguments?.start ?? CalendarDateFilter.getMinValue(columns));
    const date = startMoment.format(DATE_FORMAT);
    // combination of dataset ID and column name should be unique in a global filter bar
    // this ID is used to associate input and label elements for accessibility
    const fieldId = `gfb-filter--${BaseFilter.getFilterName(dirtyFilter, columns).replaceAll(/[^A-Za-z0-9]/g, '')}--${uniqueId()}`;

    // common date picker element props
    const datePickerProps = {
      value: date,
      'on-forge-date-picker-change': (event: CustomEvent) => {
        onChangeSingleSelectDay(event.detail);
      },
      // show Today button only in View mode
      showToday: isReadOnly,
      valueMode: 'iso-string',
      // disable in edit mode only if the other radio button is selected
      disabled: !isReadOnly && calendarDateFilterType === FILTER_TYPES.RELATIVE,
    };
    const datePickerElement = (
      <ForgeDatePicker {...datePickerProps}>
        <ForgeTextField>
          <input type="text" id={fieldId} />
          <label htmlFor={fieldId}>{BaseFilter.getFilterName(dirtyFilter, columns)}</label>
        </ForgeTextField>
      </ForgeDatePicker>
    );

    // in view mode, only display the date picker.
    // in edit mode, display the date picker and an option for SoQL literal `today` value
    if (isReadOnly) {
      return <div className="range-filter-container">{datePickerElement}</div>;
    }

    return (
      <div className="range-filter-container">
        <div
          className="radio-button-group"
          role="radiogroup"
          aria-label={I18n.t('shared.components.filter_bar.config.filter_selection')}
        >
          <div className="radio-next-to-content-container">
            <ForgeRadio
              id={`${fieldId}-date`}
              onChange={handleRadioChange}
              checked={calendarDateFilterType !== FILTER_TYPES.RELATIVE}
              name={`${fieldId}-selector`}
              value={FILTER_TYPES.RANGE}
            />
            {datePickerElement}
          </div>
          <div className="radio-next-to-content-container">
            <ForgeRadio
              id={`${fieldId}-today`}
              onChange={handleRadioChange}
              checked={calendarDateFilterType === FILTER_TYPES.RELATIVE}
              name={`${fieldId}-selector`}
              value={FILTER_TYPES.RELATIVE}
            />
            {I18n.t('relative_periods.today', { scope })}
          </div>
        </div>
      </div>
    );
  };

  const renderSingleSelectMonthPicker = () => {
    const startMoment = moment(dirtyFilter.arguments?.start);

    const monthDropdownProps = {
      label: I18n.t('select_month', { scope }),
      value: startMoment.month().toString(),
      'on-change': (event: CustomEvent) => {
        onChangeSingleSelectMonthMonth({ value: Number.parseInt(event.detail) });
      },
      options: toSelectOptions(getMonthOptions(), 'date-filter-month-option'),
      className: 'single-select-month-dropdown-month',
      popupClasses: 'forge-select__popup',
    };

    const yearDropdownProps = {
      label: I18n.t('select_year', { scope }),
      value: startMoment.year().toString(),
      'on-change': (event: CustomEvent) => {
        onChangeSingleSelectMonthYear({ value: Number.parseInt(event.detail) });
      },
      options: toSelectOptions(getYearOptions(columns, dirtyFilter), 'date-filter-year-option'),
      className: 'single-select-month-dropdown-year',
      popupClasses: 'forge-select__popup',
    };

    return (
      <div className="range-filter-container">
        <div className="single-select-month-picker">
          <div>
            <ForgeSelect {...monthDropdownProps} />
          </div>
          <div>
            <ForgeSelect {...yearDropdownProps} />
          </div>
        </div>
      </div>
    );
  };

  const renderSingleSelectYearPicker = () => {
    const selectedYear =
      getSingleSelectBy(dirtyFilter) === SINGLE_SELECT_BY.FISCAL_YEAR
        ? getFiscalEndYear(dirtyFilter.arguments?.end)
        : moment(dirtyFilter.arguments?.start).year();

    return (
      <div className="picklist-options-container">
        <Picklist
          onChange={onChangeSingleSelectYear}
          onSelection={onChangeSingleSelectYear}
          options={getYearOptions(columns, dirtyFilter)}
          size={PicklistSizes.SMALL}
          enableForgeStyle
          value={selectedYear}
        />
      </div>
    );
  };

  const renderDateRangePicker = () => {
    // combination of dataset ID and column name should be unique in a global filter bar
    // this ID is used to associate input and label elements for accessibility
    const fieldId = `gfb-filter--${BaseFilter.getFilterName(dirtyFilter, columns).replaceAll(/[^A-Za-z0-9]/g, '')}--${
      dataProvider[0]?.datasetUid
    }`;

    // Only provide the start and end dates initially when the component is first rendered.
    // Previously, continuous setStates were occurring with each key press while the user typed
    // in the input fields. This caused the inputs to rerender with new dates that were valid
    // but not what the user intended
    const previousStartValue = (document.getElementById(`${fieldId}-start`) as HTMLInputElement)?.value;
    const previousEndValue = (document.getElementById(`${fieldId}-end`) as HTMLInputElement)?.value;
    const startMoment = getMoment(dirtyFilter.arguments?.start ?? CalendarDateFilter.getMinValue(columns));
    const endMoment = getMoment(dirtyFilter.arguments?.end ?? CalendarDateFilter.getMaxValue(columns));
    const startDate = isUndefined(previousStartValue) ? startMoment.format(DATE_FORMAT) : undefined;
    const endDate = isUndefined(previousEndValue) ? endMoment.format(DATE_FORMAT) : undefined;

    // common date picker element props
    const datePickerProps = {
      ...(startDate ? { from: startDate } : {}), // if startDate is undefined, the from key will not be added to the object
      ...(endDate ? { to: endDate } : {}),
      'on-forge-date-range-picker-change': (event: CustomEvent) => {
        onChangeDateRange(event.detail);
      },
      // show Today button only in View mode
      showToday: isReadOnly,
      valueMode: 'iso-string',
      disabled: CalendarDateFilter.isRelative(dirtyFilter),
    };

    return (
      <ForgeDateRangePicker {...datePickerProps}>
        <ForgeTextField>
          <input type="text" id={`${fieldId}-start`} placeholder="mm/dd/yyyy" />
          <input type="text" id={`${fieldId}-end`} placeholder="mm/dd/yyyy" />
          <label htmlFor={fieldId}>{BaseFilter.getFilterName(dirtyFilter, columns)}</label>
        </ForgeTextField>
      </ForgeDateRangePicker>
    );
  };

  const renderRelativeTimePeriod = () => {
    if (!relativeDateOptions) {
      return null;
    }

    const defaultFilterOption = RELATIVE_FILTER_VALUES[RELATIVE_FILTERS.TODAY];

    const dropdownProps = {
      label: I18n.t('relative_date_label', { scope }),
      value: dirtyFilter.arguments?.type ?? defaultFilterOption.type,
      'on-change': (event: CustomEvent) => {
        // trigger popup repositioning logic
        if (typeof popupRef?.current?.position === 'function') {
          popupRef.current.position();
        }
        updateDirtyFilter({ type: event.detail, calendarDateFilterType: FILTER_TYPES.RELATIVE });
      },
      // add a test id to each option
      options: relativeDateOptions.map((v) => ({
        ...v,
        elementAttributes: new Map([['data-testId', `relative-date-option-${v.value}`]]),
      })),
      popupClasses: 'forge-select__popup',
      disabled: !CalendarDateFilter.isRelative(dirtyFilter),
    };

    return (
      <div>
        <div className="relative-date-container">
          <div className="relative-period">
            <ForgeSelect {...dropdownProps} />
          </div>
        </div>
      </div>
    );
  };

  const renderCustomTimePeriod = () => {
    const calendarDateFilterType = dirtyFilter.arguments?.calendarDateFilterType ?? FILTER_TYPES.RANGE;
    const relativeDateValue = dirtyFilter.arguments?.value;

    if (dirtyFilter.arguments?.type !== RELATIVE_FILTERS.CUSTOM) {
      return null;
    }

    // This is used to determine pluralization of labels only. It's ok if it's NaN.
    const count = Number.parseInt(relativeDateValue as string, 10);

    const fyFiltersEnabled = window.socrata?.fiscalYearConfig?.fy_filters_enabled ?? false;
    const options = [
      { label: I18n.t('duration_periods.day', { scope, count }), value: 'day' },
      { label: I18n.t('duration_periods.month', { scope, count }), value: 'month' },
      { label: I18n.t('duration_periods.quarter', { scope, count }), value: 'quarter' },
    ];

    // append appropriate "Year" options based on whether FY Filters are enabled
    if (fyFiltersEnabled) {
      options.push(
        { label: I18n.t('duration_periods.calendar_year', { scope, count }), value: 'calendar_year' },
        { label: I18n.t('duration_periods.fiscal_year', { scope, count }), value: 'fiscal_year' },
      );
    } else {
      options.push({ label: I18n.t('duration_periods.year', { scope, count }), value: 'year' });
    }

    const dropdownProps = {
      'aria-label': I18n.t('time_unit', { scope }),
      disabled: calendarDateFilterType !== FILTER_TYPES.RELATIVE,
      'on-change': (event: CustomEvent) => {
        updateDirtyFilter({ period: event.detail });
      },
      options,
      popupClasses: 'forge-select__popup',
      value: dirtyFilter.arguments?.period,
    };

    return (
      <>
        <div className="relative-last-value">
          <ForgeTextField>
            <input
              id="custom-period-value"
              min={CUSTOM_PERIOD_INPUT_FIELD.MIN}
              disabled={calendarDateFilterType !== FILTER_TYPES.RELATIVE}
              step={CUSTOM_PERIOD_INPUT_FIELD.STEP}
              onKeyDown={onKeyDownInputBox}
              onChange={(event) => updateDirtyFilter({ value: event.target.value })}
              type="number"
              value={relativeDateValue}
            />
          </ForgeTextField>
          {/* this label is placed outside ForgeTextField to be not displayed */}
          <label className="screenreader-only" htmlFor="custom-period-value">
            {I18n.t('last_field_value_label', { scope })}
          </label>
        </div>
        <div className="relative-last-unit">
          <ForgeSelect {...dropdownProps} />
        </div>
      </>
    );
  };

  const renderDateToTodayDatePicker = () => {
    if (dirtyFilter.arguments?.type !== RELATIVE_FILTERS.DATE_TO_TODAY) {
      return null;
    }

    const startMoment = getMoment(dirtyFilter.arguments?.start ?? CalendarDateFilter.getMinValue(columns));
    const date = startMoment.format(DATE_FORMAT);
    // combination of dataset ID and column name should be unique in a global filter bar
    // this ID is used to associate input and label elements for accessibility
    const fieldId = `gfb-filter--${BaseFilter.getFilterName(dirtyFilter, columns).replaceAll(/[^A-Za-z0-9]/g, '')}--${uniqueId()}`;

    // common date picker element props
    const datePickerProps = {
      value: date,
      'on-forge-date-picker-change': (event: CustomEvent) => {
        onChangeDateRangeToToday(event.detail);
      },
      // show Today button only in View mode
      showToday: isReadOnly,
      valueMode: 'iso-string',
    };

    return (
      <div className="range-filter-date-to-today">
        <div>
          <ForgeDatePicker {...datePickerProps}>
            <ForgeTextField>
              <input type="text" id={fieldId} />
              <label htmlFor={fieldId}>{I18n.t('start_date', { scope })}</label>
            </ForgeTextField>
          </ForgeDatePicker>
        </div>
        <div>&ndash;</div>
        <div>{I18n.t('relative_periods.today', { scope })}</div>
      </div>
    );
  };

  const renderDateRangeAndRelativeTimePeriod = () => {
    const showCustomTimePeriod = dirtyFilter.arguments?.type === RELATIVE_FILTERS.CUSTOM;

    const fieldId = `gfb-filter--${BaseFilter.getFilterName(dirtyFilter, columns).replaceAll(/[^A-Za-z0-9]/g, '')}--${uniqueId()}-filter`;

    return (
      <div className="range-filter-container">
        <form className="filter-options">
          <div
            className="radio-button-group"
            role="radiogroup"
            aria-label={I18n.t('shared.components.filter_bar.config.filter_selection')}
          >
            <div className="radio-next-to-content-container">
              <ForgeRadio
                id={`${fieldId}-range`}
                onChange={handleRadioChange}
                checked={!CalendarDateFilter.isRelative(dirtyFilter)}
                name={`${fieldId}-selector`}
                value={FILTER_TYPES.RANGE}
                aria-label={I18n.t('date_range_label', { scope })}
              />
              {renderDateRangePicker()}
            </div>
            {(relativeDateOptions || showCustomTimePeriod) && (
              <div className="radio-next-to-content-container">
                <ForgeRadio
                  id={`${fieldId}-relative`}
                  onChange={handleRadioChange}
                  checked={CalendarDateFilter.isRelative(dirtyFilter)}
                  name={`${fieldId}-selector`}
                  value={FILTER_TYPES.RELATIVE}
                  aria-label={I18n.t('relative_date_label', { scope })}
                />
                <div className="range-filter-relative">
                  {renderRelativeTimePeriod()}
                  {renderCustomTimePeriod()}
                </div>
              </div>
            )}
          </div>
          {renderDateToTodayDatePicker()}
        </form>
      </div>
    );
  };

  const singleSelectBy = getSingleSelectBy(dirtyFilter);
  const isSingleSelectYear =
    singleSelectBy === SINGLE_SELECT_BY.YEAR || singleSelectBy === SINGLE_SELECT_BY.FISCAL_YEAR;

  const renderControls = () => {
    // Fiscal year has the exact same picker style as year
    if (isSingleSelectYear) {
      return renderSingleSelectYearPicker();
    }

    switch (singleSelectBy) {
      case SINGLE_SELECT_BY.MONTH:
        return renderSingleSelectMonthPicker();
      case SINGLE_SELECT_BY.DAY:
        return renderSingleSelectDayPicker();
      default:
        return renderDateRangeAndRelativeTimePeriod();
    }
  };

  return (
    <div className="filter-controls calendar-date-filter" ref={dateFilterRef}>
      {singleSelectBy === SINGLE_SELECT_BY.FISCAL_YEAR && (
        <span className="single-select-fiscal-year-label">
          {I18n.t('shared.components.filter_bar.config.fiscal_year_label')}
        </span>
      )}
      {renderControls()}
      <FilterFooter
        showApplyButton={!isSingleSelectYear}
        disableApplyFilter={!isDirty()}
        isDrilldown={dirtyFilter.isDrilldown}
        isReadOnly={isReadOnly}
        onClickApply={applyFilter}
        onClickRemove={onRemove}
        onClickReset={resetFilter}
        showRemoveButton={showRemoveButtonInFooter}
      />
    </div>
  );
}
